// @ts-strict-ignore
import { useTranslation } from "react-i18next";

import { Typography } from "@aviary";
import { useFlippers } from "@shared/context/FlippersContext";
import { BackButton } from "@shared/layouts";
import { useLocation, useSearchParams } from "@shared/react-router-dom/react-router-dom";
import type { PatientAttributes } from "@unauthenticated/shared/data/types/PatientAttributes.d";
import { CareContinuityBanner } from "@unauthenticated/signupSignin/components/CareContinuityBanner/CareContinuityBanner";
import { InvitedByBanner } from "@unauthenticated/signupSignin/components/InvitedByBanner/InvitedByBanner";
import { PatientStoreSignUpForm } from "@unauthenticated/signupSignin/components/PatientStoreSignUpForm/PatientStoreSignUpForm";
import { PoweredByFullscript } from "@unauthenticated/signupSignin/components/PoweredByFullscript/PoweredByFullscript";
import { SignUpHeader } from "@unauthenticated/signupSignin/components/SignUpHeader/SignUpHeader";
import { SignUpSubtitle } from "@unauthenticated/signupSignin/components/SignUpSubtitle/SignUpSubtitle";
import { SignUpTitle } from "@unauthenticated/signupSignin/components/SignUpTitle/SignUpTitle";
import { StoreLandingLayout } from "@unauthenticated/signupSignin/components/StoreLandingLayout/StoreLandingLayout";
import { StoreLogo } from "@unauthenticated/signupSignin/components/StoreLogo/StoreLogo";
import { useCareContinuityRedirect } from "@unauthenticated/signupSignin/hooks/useCareContinuityRedirect/useCareContinuityRedirect";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./PatientStoreSignUpPage.styles";

interface HistoryState {
  source?: "RX_SIGN_IN";
  email?: string;
}

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  pageVersion?: string;
  storeId?: string;
  isPreview?: boolean;
  isStoreOwner: boolean;
  ssoErrors?: string;
  activePatientPromotion?: boolean;
  isStorefronts?: boolean;
}

const PatientStoreSignUpPage = ({
  patientAttributes,
  additionalAttributes,
  pageVersion,
  isPreview = false,
  isStoreOwner,
  storeId,
  ssoErrors,
  activePatientPromotion,
  isStorefronts,
}: Props) => {
  const [isQualityStorySignupEnabled] = useFlippers("platypus_quality_story_signup");
  const { t } = useTranslation();
  const { storeLogo, organizationLogo, storeName, pracAvatar, isStoreIntake, iconLogo, storeSlug } =
    patientAttributes;
  const { state }: { state: HistoryState } = useLocation();
  const [searchParameters] = useSearchParams();

  const isIntake = searchParameters.get("intake") === "true";
  const requestedPractitionerId = searchParameters.get("requestedPractitionerId");

  const isCareContinuityRedirect = useCareContinuityRedirect();

  const renderIntakeSubtitle = () => {
    if (!isStoreIntake || !isIntake || isStorefronts) {
      return null;
    }

    return <Typography css={styles.centered}>{t(l.intake.signUpDescription)}</Typography>;
  };

  const renderCreateAccountSubtitle = () => {
    if ((isStoreIntake && isIntake) || isStorefronts) {
      return null;
    }

    if (isSwiftFillSignIn) {
      return (
        <>
          <Typography type="h4">{t(l.auth.createYourAccount)}</Typography>
          <Typography css={styles.centered}>{t(l.auth.swiftfillSignupInstructions)}</Typography>
        </>
      );
    }
  };

  const isSwiftFillSignIn = state?.source === "RX_SIGN_IN";

  const signUpTitle = isStorefronts ? t(l.auth.createYourAccount) : storeName;
  const showQualityStoryChrome = !isStorefronts && !!isQualityStorySignupEnabled;

  return (
    <StoreLandingLayout
      storeName={storeName}
      storeId={storeId}
      storeSlug={storeSlug}
      pracAvatar={pracAvatar}
      isPreview={isPreview}
      isStoreOwner={isStoreOwner}
      isActivePromotion={activePatientPromotion}
      isStorefronts={isStorefronts}
      organizationLogo={organizationLogo}
      iconLogo={iconLogo}
      showDeliveryCountry
      hasFullBleedPanel
    >
      {({ isFullBleed }) => (
        <>
          <SignUpHeader isStorefronts={isStorefronts} hasCompactSpacing={showQualityStoryChrome}>
            {!!isStorefronts && (
              <div css={styles.backButtonWrapper}>
                <BackButton text={t(l.common.Back)} isUnderLined={false} />
              </div>
            )}
            {!isStorefronts && <StoreLogo storeLogo={storeLogo} storeName={storeName} />}
            <SignUpTitle title={signUpTitle} />
            {!!showQualityStoryChrome && (
              <SignUpSubtitle subtitle={t(l.patientRxAuth.QualityStorySubtitle)} />
            )}
            {renderIntakeSubtitle()}
            {renderCreateAccountSubtitle()}
          </SignUpHeader>
          {!!isCareContinuityRedirect && (
            <div css={styles.careContinuityWrapper}>
              {!isStorefronts && <Typography type="h4">{t(l.auth.createYourAccount)}</Typography>}
              <CareContinuityBanner />
            </div>
          )}
          <InvitedByBanner storeName={storeName} />
          <PatientStoreSignUpForm
            isStorefronts={isStorefronts}
            storeId={storeId}
            pageVersion={pageVersion}
            patientAttributes={patientAttributes}
            additionalAttributes={additionalAttributes}
            isPreview={isPreview}
            ssoErrors={ssoErrors}
            preFilledEmail={state?.email}
            isSwiftFillSignIn={isSwiftFillSignIn}
            isIntake={isIntake}
            requestedPractitionerId={requestedPractitionerId}
            isFullWidthButtons={isFullBleed}
          />
          {!!showQualityStoryChrome && <PoweredByFullscript />}
        </>
      )}
    </StoreLandingLayout>
  );
};

export { PatientStoreSignUpPage };
